import React, { useEffect } from 'react';
import styled from 'styled-components/macro';
import * as colors from 'src/styles/colors';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { mobilePoint, modalZIndex } from 'src/styles/globalStyle';
import { useStore, useModalStore } from 'src/hooks/useStore';
import InformModal from 'src/components/contents/Inform/InformModal';

import LeftLogo from 'src/assets/images/tipo_logo.png';
import RightNavIconMsg from 'src/assets/icon/menu_message.svg';
import RightNavIconSite from 'src/assets/icon/menu_sitemap.svg';
import RightNavIconMail from 'src/assets/icon/menu_mail.svg';
import { FaBars } from 'react-icons/fa';
import { globalComponents } from 'src/types/globalTypes';

const HeaderWrapper = styled.div<{ isActive: boolean }>`
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  @media (max-width: ${mobilePoint}) {
    justify-content: right;
    position: ${(props) => (props.isActive ? 'unset' : 'absolute')};
    z-index: ${modalZIndex};
    right: 0px;
    color: ${(props) => (props.isActive ? colors.BlueGray_700 : colors.white)};
    padding: 12px 12px;
  }
`;
const LeftHeader = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: ${mobilePoint}) {
    display: none;
  }
`;
const RightHeader = styled.div`
  display: flex;
  align-items: center;
  @media (max-width: ${mobilePoint}) {
    display: none;
  }
`;
const LeftIcon = styled.img`
  height: 35px;
`;
const Title = styled.div`
  display: flex;
  align-items: center;
  padding: 0 12px;
`;
const TitleText = styled.span`
  font-family: NotoSansTC-Regular;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  color: ${colors.BlueGray_700};
  padding-left: 10px;
  white-space: nowrap;
`;
const LeftItem = styled.div<{ color: string; isActive: boolean }>`
  background-color: ${(props) => props.isActive && props.color};
  color: ${(props) => props.isActive && colors.white};
  border-radius: 20px;
  height: 40px;
  width: 96px;
  cursor: pointer;
  display: flex;
  align-items: center;
`;
const LeftText = styled.span`
  font-family: NotoSansTC-Regular;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  margin: auto;
`;
const RightItem = styled.div`
  display: flex;
  align-items: center;
  margin-left: 25px;
  cursor: pointer;
`;
const RightIcon = styled.img`
  margin-right: 5px;
`;
const RightText = styled.span`
  font-size: 16px;
  line-height: 23px;
  color: ${colors.BlueGray_700};
`;

const HamburgerIcon = styled.span`
  display: none;
  @media (max-width: ${mobilePoint}) {
    display: block;
  }
`;

interface IProps {
  setMobileMenuIsOpen: (isOpen: boolean) => void;
}

/**
 * 網站 Header。
 */
const Header: React.FC<IProps> = ({ setMobileMenuIsOpen }) => {
  const checkWindowWidth = useStore((state) => state.checkWindowWidth);
  const setActiveModal = useModalStore((state) => state.setActiveModal);

  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  
  const handleKeyDown = (
    e: React.KeyboardEvent<Element>, 
    callback: () => void
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback();
    }
  };

  // 監聽視窗大小改變
  useEffect(() => {
    const handleResize = () => checkWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [checkWindowWidth]);

  // 改變背景顏色
  if (pathname.startsWith(process.env.PUBLIC_URL + '/patentRights')) {
    if (pathname === process.env.PUBLIC_URL + '/patentRights/result') {
      document.body.style.backgroundColor = colors.white;
    } else {
      document.body.style.backgroundColor = '#FBF7FD';
    }
  } else if (pathname.startsWith(process.env.PUBLIC_URL + '/trademarkRights')) {
    if (pathname === process.env.PUBLIC_URL + '/trademarkRights/result') {
      document.body.style.backgroundColor = colors.white;
    } else {
      document.body.style.backgroundColor = '#EEF5FF';
    }
  } else if (pathname.startsWith(process.env.PUBLIC_URL + '/certificate')) {
    document.body.style.backgroundColor = '#FEFBF8';
  } else {
    document.body.style.backgroundColor = '#F6FAFF';
  }

  // 因應GA統計顯示各網址中文標題，置換網站標題
  useEffect(() => {
    const getTitle = (path: string, searchParams: string) => {
      // 檢測是否為QRcode進入
      const regex = /[QRcode=Y]/;
      switch (path) {
		case process.env.PUBLIC_URL + '/sitemap':
		    return '權證查詢系統-網站導覽';
        case process.env.PUBLIC_URL + '/patentRights/detail':
          if (regex.test(searchParams)) {
            return '權證查詢系統-專利權證案件明細(QRcode)';
          } else {
            return '權證查詢系統-專利權證案件明細';
          }
        case process.env.PUBLIC_URL + '/patentRights/result':
          return '權證查詢系統-專利權證查詢結果';
        case process.env.PUBLIC_URL + '/patentRights/direction':
          return '權證查詢系統-專利權證操作說明';
        case process.env.PUBLIC_URL + '/patentRights':
          return '權證查詢系統-專利權證查詢';
        case process.env.PUBLIC_URL + '/trademarkRights/detail':
          if (regex.test(searchParams)) {
            return '權證查詢系統-商標權證案件明細(QRcode)';
          } else {
            return '權證查詢系統-商標權證案件明細';
          }
        case process.env.PUBLIC_URL + '/trademarkRights/result':
          return '權證查詢系統-商標權證查詢結果';
        case process.env.PUBLIC_URL + '/trademarkRights/direction':
          return '權證查詢系統-商標權證操作說明';
        case process.env.PUBLIC_URL + '/trademarkRights':
          return '權證查詢系統-商標權證查詢';
        case process.env.PUBLIC_URL + '/certificate/verify':
          return '權證查詢系統-電子證書驗證';
        case process.env.PUBLIC_URL + '/certificate/download':
          return '權證查詢系統-電子證書下載';
        case process.env.PUBLIC_URL + '/certificate/direction':
          return '權證查詢系統-電子證書操作說明';
        case process.env.PUBLIC_URL + '/qrcode/direction':
          return '權證查詢系統-QRcode操作說明';
        case process.env.PUBLIC_URL || '/':
          return '權證查詢系統';
        default:
          return '權證查詢系統';
      }
    };
    document.title = getTitle(pathname, search);
  }, [pathname, search]);
  return (
    <HeaderWrapper isActive={pathname === (process.env.PUBLIC_URL === '' ? "/" : process.env.PUBLIC_URL)}>
      <LeftHeader>
        <Link to={process.env.PUBLIC_URL}>
          <Title>
            <LeftIcon src={LeftLogo} alt="經濟部智慧財產局權證查詢系統 LOGO" />
            <TitleText>權證查詢系統</TitleText>
          </Title>
        </Link>
        {pathname !== (process.env.PUBLIC_URL === '' ? "/" : process.env.PUBLIC_URL) && (
          <>
            <LeftItem
              color={globalComponents.P.color.seventh}
              isActive={pathname.startsWith(process.env.PUBLIC_URL + '/patentRights')}
              onClick={() => navigate(process.env.PUBLIC_URL + '/patentRights')}
			  tabIndex={0}
			  role="button"
			  onKeyDown={(e) => handleKeyDown(e, () => navigate(process.env.PUBLIC_URL + '/patentRights'))}
            >
              <LeftText>專利權證</LeftText>
            </LeftItem>
            <LeftItem
              color={globalComponents.T.color.seventh}
              isActive={pathname.startsWith(process.env.PUBLIC_URL + '/trademarkRights')}
              onClick={() => navigate(process.env.PUBLIC_URL + '/trademarkRights')}
			  tabIndex={0}
			  role="button"
			  onKeyDown={(e) => handleKeyDown(e, () => navigate(process.env.PUBLIC_URL + '/trademarkRights'))}
            >
              <LeftText>商標權證</LeftText>
            </LeftItem>
            <LeftItem
              color={globalComponents.C.color.fifth}
              isActive={pathname.startsWith(process.env.PUBLIC_URL + '/certificate')}
              onClick={() => navigate(process.env.PUBLIC_URL + '/certificate/verify')}
			  tabIndex={0}
			  role="button"
			  onKeyDown={(e) => handleKeyDown(e, () => navigate(process.env.PUBLIC_URL + '/certificate/verify'))}
            >
              <LeftText>電子證書</LeftText>
        </LeftItem>
          </>
        )}
      </LeftHeader>
      <RightHeader>
        <RightItem tabIndex={0} role="button" onClick={() => navigate(process.env.PUBLIC_URL + '/patentRights/direction')} onKeyDown={(e) => handleKeyDown(e, () => navigate(process.env.PUBLIC_URL + '/patentRights/direction'))}>
          <RightIcon src={RightNavIconMsg} alt="" />
          <RightText>操作說明</RightText>
        </RightItem>
		<RightItem tabIndex={0} role="button" onClick={() => navigate(process.env.PUBLIC_URL + '/sitemap')} onKeyDown={(e) => handleKeyDown(e, () => navigate(process.env.PUBLIC_URL + '/sitemap'))}>
          <RightIcon src={RightNavIconSite} alt="" />
          <RightText>網站導覽</RightText>
        </RightItem>
        <RightItem tabIndex={0} role="button" aria-label="開啟彈跳視窗資料錯誤通報" onClick={() => setActiveModal(<InformModal />)} onKeyDown={(e) => handleKeyDown(e, () => setActiveModal(<InformModal />))}>
          <RightIcon src={RightNavIconMail} alt="" />
          <RightText>資料錯誤通報</RightText>
        </RightItem>
      </RightHeader>
      <HamburgerIcon tabIndex={0} role="button" aria-label="開啟選單" onClick={() => setMobileMenuIsOpen(true)} onKeyDown={(e) => handleKeyDown(e, () => setMobileMenuIsOpen(true))} >
        <FaBars />
      </HamburgerIcon>
    </HeaderWrapper>
  );
};

export default Header;
